<!--
  CompanyEinField -- the distributor's EIN (tax id), write-only.

  The full EIN is never returned by the API, so this control never has a
  value to show: it shows "stored, ends 6789" (masked XX-XXX6789) or "Not
  provided", and the input only ever holds what the user is typing right now.

  Two modes, because the two pages that host it save differently:
    - "staged"    (Company page, ONE Save): Replace / Remove only STAGE a
                  change; the page's Save calls validate() then commit().
    - "immediate" (SA edit page, autosaving, no page Save): Replace and
                  Remove apply as soon as they are confirmed here.

  Fails closed in the UI the same way the API does: when the server reports
  replace_available=false (no usable encryption key) Replace is disabled with
  a plain message; Remove stays available because it needs no key.

  Exposes validate() -> boolean and commit() -> Promise<{ok, error?}>.
-->
<template>
  <div data-testid="company-ein-section">
    <p v-if="loading" data-testid="company-ein-loading" class="text-medium-emphasis">
      Loading tax ID...
    </p>

    <template v-else>
      <p data-testid="company-ein-status" class="mb-2">
        <template v-if="staged && staged.action === 'clear'">
          Tax ID will be removed when you save.
        </template>
        <template v-else-if="staged && staged.action === 'set'">
          New tax ID ready to save.
        </template>
        <template v-else-if="status.has_ein">
          Stored: {{ maskedEin }}
        </template>
        <template v-else>Not provided.</template>
      </p>

      <p
        v-if="!status.replace_available"
        data-testid="company-ein-unavailable"
        class="text-medium-emphasis mb-2"
      >
        Tax ID entry isn't enabled in this environment yet.
      </p>

      <div v-if="editing">
        <v-text-field
          v-model="draft"
          label="EIN"
          placeholder="12-3456789"
          inputmode="numeric"
          autocomplete="off"
          maxlength="12"
          :error-messages="inputError ? [inputError] : []"
          data-testid="company-ein-input"
          @keydown.enter.prevent="onConfirm"
        ></v-text-field>
        <div class="d-flex ga-2 mb-2">
          <v-btn
            size="small"
            variant="flat"
            color="primary"
            :disabled="busy"
            data-testid="company-ein-confirm"
            @click="onConfirm"
          >
            {{ mode === "immediate" ? "Save tax ID" : "Use this EIN" }}
          </v-btn>
          <v-btn
            size="small"
            variant="text"
            :disabled="busy"
            data-testid="company-ein-cancel"
            @click="cancelEdit"
          >
            Cancel
          </v-btn>
        </div>
      </div>

      <div v-else class="d-flex ga-2 mb-2">
        <v-btn
          v-if="canEdit"
          size="small"
          variant="outlined"
          :disabled="!status.replace_available || busy"
          data-testid="company-ein-replace"
          @click="startEdit"
        >
          {{ status.has_ein ? "Replace" : "Add EIN" }}
        </v-btn>
        <v-btn
          v-if="canEdit && status.has_ein && !(staged && staged.action === 'clear')"
          size="small"
          variant="text"
          color="error"
          :disabled="busy"
          data-testid="company-ein-remove"
          @click="onRemove"
        >
          Remove
        </v-btn>
        <v-btn
          v-if="staged"
          size="small"
          variant="text"
          data-testid="company-ein-undo"
          @click="undo"
        >
          Undo
        </v-btn>
      </div>

      <p
        v-if="error"
        data-testid="company-ein-error"
        class="text-error mb-2"
      >
        {{ error }}
      </p>
      <p
        v-if="savedNote"
        data-testid="company-ein-saved"
        class="text-success mb-2"
      >
        {{ savedNote }}
      </p>

      <p data-testid="company-ein-explainer" class="text-medium-emphasis">
        Used for tax and billing records. Stored encrypted; ThreadFlow staff
        only see the last 4 digits.
      </p>
    </template>
  </div>
</template>

<script>
import * as distributorService from "@/services/distributorService";

// Mirrors the API rule (api/src/services/ein_crypto.normalize_ein): nine
// ASCII digits once spaces and hyphens are removed. The server stays the
// authority; this only saves a round trip and names the problem at the field.
function einDigits(value) {
  return String(value || "").replace(/[\s-]/g, "");
}

export default {
  name: "CompanyEinField",
  props: {
    distributorId: { type: [Number, String], default: null },
    mode: { type: String, default: "staged" },
    canEdit: { type: Boolean, default: true },
  },
  emits: ["changed"],
  data() {
    return {
      loading: true,
      busy: false,
      status: { has_ein: false, ein_last4: null, replace_available: true },
      editing: false,
      draft: "",
      inputError: "",
      error: "",
      savedNote: "",
      // { action: "set", digits } | { action: "clear" } | null (staged mode)
      staged: null,
    };
  },
  computed: {
    maskedEin() {
      return this.status.ein_last4 ? `XX-XXX${this.status.ein_last4}` : "XX-XXX----";
    },
  },
  watch: {
    distributorId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    async load() {
      if (this.distributorId == null || this.distributorId === "") return;
      this.loading = true;
      this.error = "";
      try {
        const resp = await distributorService.getEinStatus(this.distributorId);
        this.status = { ...this.status, ...resp.data };
      } catch (err) {
        this.error = "Could not load the tax ID status. Reload the page to try again.";
      } finally {
        this.loading = false;
      }
    },
    startEdit() {
      this.editing = true;
      this.draft = "";
      this.inputError = "";
      this.savedNote = "";
    },
    cancelEdit() {
      this.editing = false;
      this.draft = "";
      this.inputError = "";
    },
    undo() {
      this.staged = null;
      this.$emit("changed", null);
    },
    onConfirm() {
      const digits = einDigits(this.draft);
      if (!/^[0-9]{9}$/.test(digits)) {
        this.inputError = "Enter 9 digits, for example 12-3456789.";
        return;
      }
      this.inputError = "";
      // Drop the typed value from the input immediately; the staged copy is
      // held privately until commit and is never rendered back.
      this.staged = { action: "set", digits };
      this.draft = "";
      this.editing = false;
      this.$emit("changed", { action: "set" });
      if (this.mode === "immediate") this.commit();
    },
    onRemove() {
      this.staged = { action: "clear" };
      this.$emit("changed", { action: "clear" });
      if (this.mode === "immediate") this.commit();
    },
    /** True when the page may proceed to save (nothing half-typed). */
    validate() {
      if (this.editing && einDigits(this.draft) !== "") {
        // Typed but not confirmed: treat the typed value as the replacement.
        this.onConfirm();
        return this.inputError === "";
      }
      return true;
    },
    /** Apply the staged change. Resolves { ok, error? }; never throws. */
    async commit() {
      if (!this.staged) return { ok: true };
      this.busy = true;
      this.error = "";
      const change = this.staged;
      try {
        const resp =
          change.action === "set"
            ? await distributorService.setEin(this.distributorId, change.digits)
            : await distributorService.clearEin(this.distributorId);
        this.status = { ...this.status, ...resp.data };
        this.staged = null;
        this.savedNote = change.action === "set" ? "Tax ID saved." : "Tax ID removed.";
        this.$emit("changed", null);
        return { ok: true };
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          "The tax ID could not be saved. Nothing was changed.";
        this.error = message;
        if (this.mode === "immediate") this.staged = null;
        return { ok: false, error: message };
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
